import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { Lesson } from "@edu/shared";
import { Empty } from "../../lib/Empty.tsx";
import { lessonChatId } from "../occurrence-chat/occurrence-chat-api.ts";
import { useOccurrenceChat } from "../occurrence-chat/use-occurrence-chat.ts";
import { LessonRow } from "./LessonRow.tsx";
import { lessonErrorText } from "./lesson-errors.ts";
import { groupLessons } from "./lesson-when.ts";

/**
 * Список уроков.
 *
 * Один список на обе роли: сервер отдаёт уроки, где я преподаватель ИЛИ где я
 * ученик, и второй ветки в предикате нет. Ученик не видит чужие уроки не
 * потому, что так написан этот компонент.
 *
 * ⚠️ **Формы создания здесь больше нет** (решение владельца 17.09.2026). Она
 * заводила урок без даты и времени, то есть урок, которого не видно в
 * расписании; теперь занятие заводится тем же окном, что и из сетки, а зовёт
 * его кнопка в шапке экрана. Панель осталась чистым списком, и учеников с
 * заготовками ей больше не нужно.
 *
 * Своего заголовка у панели нет: его несёт шапка экрана. Раньше панель была
 * половиной общей главной и называла себя сама.
 */
export function LessonsPanel(props: {
  lessons: Lesson[];
  /**
   * Может ли смотрящий заводить занятия.
   *
   * ⚠️ У ученика этого нет вовсе, и дело не только в кнопке: ручка создания
   * берёт преподавателя из сессии и его паре занятие не заведёт в принципе.
   * Список уроков при этом остаётся общим на обе роли.
   */
  canCreate: boolean;
  /** Открыть окно нового занятия. То же действие, что у кнопки в шапке. */
  onCreate: () => void;
  onChanged: () => void;
  /**
   * Под списком: догрузка завершённых. Они последняя группа, поэтому место под
   * списком и есть место под ними.
   */
  footer?: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * Урок, чей чат сейчас открыт. Одна лента на весь список.
   *
   * ⚠️ **Хук стоит ЗДЕСЬ, а не в строке, и причин две** (раздел 12
   * `docs/room-chat-design.md`). Свой хук на строку означал бы двести таймеров
   * и двести опросов в списке из двухсот уроков. И черновик: набранное и не
   * отправленное обязано пережить сворачивание строки, а живя в строке, оно
   * исчезало бы вместе с ней. Черновики хранятся по адресу чата, поэтому
   * переход к другому уроку и обратно ничего не теряет и ничего не путает.
   *
   * ⚠️ Адрес собирает `lessonChatId`: из списка уроков чат адресуется УРОКОМ, а
   * занятие серии сервер разрешает из урока в ключ пары либо группы сам.
   */
  const [chatOf, setChatOf] = useState<string | null>(null);
  const chat = useOccurrenceChat({
    address: chatOf === null ? null : lessonChatId(chatOf),
    open: chatOf !== null,
  });

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      props.onChanged();
    } catch (e) {
      setErr(lessonErrorText(e));
    } finally {
      setBusy(false);
    }
  }

  // Часы берём один раз на отрисовку, а не в каждой строке: в списке они все
  // равно обязаны показывать одно и то же «сегодня»
  const now = Date.now();
  // Тот же момент уезжает и в порядок строк: он делит созданные на предстоящие
  // и прошедшие, и вторые часы здесь развели бы порядок с ярлыками
  const groups = groupLessons(props.lessons, now);

  return (
    <section className="card">
      {err && <p className="err">{err}</p>}

      {props.lessons.length === 0 ? (
        /* Пустой список отвечает на «что теперь», а не только на «сколько тут
           всего»: разбор у `emptyNote` */
        <Empty
          icon="board"
          title="Уроков пока нет"
          text={emptyNote(props.canCreate)}
          /* Кнопка плашки повторяет главное действие экрана и зовёт тот же
             обработчик: на пустом экране делать больше нечего, а шапка это
             последнее место, куда смотрит глаз (шапка `lib/Empty.tsx`).
             exactOptionalPropertyTypes: поле либо есть со значением, либо его
             нет вовсе, и `undefined` значением не считается */
          {...(props.canCreate
            ? { action: { label: "Новое занятие", onClick: props.onCreate } }
            : {})}
        />
      ) : (
        /*
          ⚠️ **`data-rows="panel"`, а не `"page"`** (решение владельца
          17.09.2026). Ниже порога колонок строка перестаёт быть стопкой слотов
          с приписками и становится двумя строками, но ячейка действий при этом
          ОСТАЁТСЯ: у `page` её прячут доводом «строка сама ведёт туда, где эти
          действия живут», а здесь имя урока не ссылка и вести ей некуда. В
          ячейке стоит вход в урок и внешний созвон, то есть режим
          сосуществования (раздел 2 CLAUDE.md), и спрятать их значит выключить
          строку. Что именно уезжает и почему, расписано по слотам в
          `LessonRow.tsx`; сама раскладка живёт в `styles.css`, в блоке
          `@media (max-width: 63.99rem)`.
        */
        <ul className="list list--lessons" data-rows="panel">
          <li className="list__head" aria-hidden="true">
            {/* Столбец шеврона: заголовка у него нет и быть не может */}
            <span />
            <span>Дата</span>
            <span>Урок</span>
            <span>Ученик</span>
            <span>Заготовка</span>
            <span>Состояние</span>
            <span />
          </li>
          {groups.map((g) => (
            <Fragment key={g.key}>
              <li className="list__group">{g.title}</li>
              {g.items.map((l) => (
                <LessonRow
                  key={l.id}
                  lesson={l}
                  now={now}
                  busy={busy}
                  run={run}
                  chat={chatOf === l.id ? chat : null}
                  onToggleChat={() => setChatOf(chatOf === l.id ? null : l.id)}
                />
              ))}
            </Fragment>
          ))}
        </ul>
      )}
      {props.footer}
    </section>
  );
}

/**
 * Что написано под заголовком пустого списка.
 *
 * ⚠️ Текст переписан 17.09.2026 вместе с уходом формы. До этого дня здесь
 * стояло «урок без даты нужен для разбора вне графика», и такого пути больше
 * нет: урок, заводимый человеком, всегда встаёт на дату и время.
 *
 * Преподавателю говорится то, чего из пустого экрана не видно: список
 * наполняется с двух сторон, и занятие повторяющегося расписания появляется в
 * нём не в момент заведения расписания, а позже. У ученика создавать нечего
 * вовсе, и пустой список для него это факт, а не призыв.
 *
 * ⚠️ **Кнопки соседнего экрана здесь не называются, а пути названы оба, и
 * фраза эта переписывалась трижды.** Сначала стояло «когда вы нажмёте
 * „Подготовить“»: одна кнопка из двух, то есть неполная правда (занятие серии
 * становится уроком и от подготовки, и от входа в комнату), и вдобавок
 * пересказ чужого экрана, который пришлось бы править вместе с ним. Потом
 * «когда вы им займётесь»: честно и бесполезно, на «что мне сделать» оно не
 * отвечает. Потом «когда вы откроете занятие»: это слово совпало с раскрытием
 * карточки в сетке, от которого в списке не появляется ничего. Теперь названы
 * два жеста, которыми урок и заводится, сбор материала заранее и вход в
 * комнату, и названы они тем, что человек делает, а не тем, как подписана
 * кнопка.
 */
function emptyNote(canCreate: boolean): ReactNode {
  if (!canCreate) return "Занятия ставит преподаватель: они появятся здесь сами.";
  return (
    <>
      Разовое занятие встаёт в список сразу. Повторяющееся живёт в{" "}
      <Link className="textlink" to="/schedule">
        расписании
      </Link>
      , а сюда попадает, когда вы соберёте к занятию материал или войдёте в комнату.
    </>
  );
}
